import {CombinedApiResponse, doApiRequest, Method, Module} from '..';
import {parseGenericFilters} from '../meta/filters';
import {ApiSortsDerived} from '../meta/odata';
import Umbuchung, {ApiUmbuchung, DetailUmbuchungFilters, UmbuchungFilters} from '@/model/Umbuchung';
import {ApiSuccessResponse} from '@/api/types';
import {ApiUmbuchungPosition, ApiUmbuchungPositionFlawed} from '@/model/UmbuchungPosition';
import {ApiBookingWarehouse} from '@/model/BookingWarehouse';

export enum ResourceUmbuchung {
  Umbuchung = '/Umbuchung',
}

export enum UmbuchungAction {
  Positionen = '/Position',
  UpdatePosition = '/Position/{posNr}',
  DeletePosition = '/Position/{posNr}',
}

export interface ApiUmbuchungSubmitPosition {
  PosNr: number;
  ArtikelNr: string;
  Menge: number;
  ArtikelBezeichnung1: string;
  ArtikelBezeichnung2?: string;
  Nabi?: boolean;
  NVE?: string;
  SerienNr?: string;
  Gebinde: {
    GebindeMenge: number | null;
    GebindeEinheit: string | null;
    GebindeSchluessel: number;
    GebindeInhalt: number | null;
    EAN_Gebinde: string | null;
  } | null;
  AbgehendesLager: ApiBookingWarehouse;
  EingehendesLager: ApiBookingWarehouse;
}

export interface ApiUmbuchungSubmitData {
  BelegNr?: number;
  Datum: string;
  Positionen: ApiUmbuchungSubmitPosition[];
  NiederlassungAbgehend: {
    Niederlassung: number;
  };
  NiederlassungEingehend: {
    Niederlassung: number;
  };
  Erledigt?: boolean;
  Bediener: string;
}

export async function getUmbuchungen(
    filters: UmbuchungFilters, sorts: ApiSortsDerived<ApiUmbuchung>,
    limit: number | null = null, skip: number = 0,
): Promise<CombinedApiResponse<Umbuchung[]>> {
  const parsedFilters = parseGenericFilters(filters);
  const response = await doApiRequest<ApiUmbuchung[]>({
    method: Method.Get,
    resource: ResourceUmbuchung.Umbuchung,
    module: Module.Lager,
    filters: parsedFilters,
    expand: {
      NiederlassungEingehend: null,
      NiederlassungAbgehend: null,
      LKW: null,
    },
    sorts,
    skip,
    limit,
  });

  return [response.Data.map((o) => Umbuchung.fromApi(o)), response.CountAll];
}

export async function getUmbuchungPositionen(
    filters: DetailUmbuchungFilters,
): Promise<CombinedApiResponse<ApiUmbuchungPosition[]>> {
  const parsedFilters = parseGenericFilters(filters);
  const response = await doApiRequest<ApiUmbuchungPositionFlawed[]>({
    method: Method.Get,
    resource: ResourceUmbuchung.Umbuchung,
    module: Module.Lager,
    filters: parsedFilters,
    action: UmbuchungAction.Positionen,
    select: new Set(['*']),
    expand: {
      Gebinde: null,
    },
    id: filters.BelegNr,
  });

  return [response.Data ? response.Data.map((p) => ({...p, VerfuegbareChargen: p.VerfuegbareChargen.Chargen})) : [], response.CountAll];
}

export async function getDetailUmbuchung(
    filters: DetailUmbuchungFilters,
): Promise<CombinedApiResponse<Umbuchung | null>> {
  const parsedFilters = parseGenericFilters(filters);
  const [umbuchungResponse, positionsResponse] = await Promise.all([
    doApiRequest({
      method: Method.Get,
      module: Module.Lager,
      resource: ResourceUmbuchung.Umbuchung,
      expand: {
        NiederlassungEingehend: null,
        NiederlassungAbgehend: null,
      },
      limit: 1,
      filters: parsedFilters,
    }),
    (async (): Promise<ApiSuccessResponse<ApiUmbuchungPositionFlawed[]>> => {
      if (!filters.BelegNr) {
        return {
          RequestId: '',
          Data: [],
          Count: 0,
          CountAll: 0,
        };
      }
      return doApiRequest({
        method: Method.Get,
        module: Module.Lager,
        resource: ResourceUmbuchung.Umbuchung,
        action: UmbuchungAction.Positionen,
        select: new Set(['*']),
        expand: {
          Gebinde: null,
          AbgehendesLager: null,
          EingehendesLager: null,
        },
        id: filters.BelegNr,
      });
    })(),
  ]);

  return umbuchungResponse.Data ? [Umbuchung.fromApi({
    ...umbuchungResponse.Data[0],
    Positionen: positionsResponse.Data.map((p) => ({...p, VerfuegbareChargen: p.VerfuegbareChargen.Chargen}))})] : [null];
}

export async function submitUmbuchung(
    data: ApiUmbuchungSubmitData,
) {
  await doApiRequest({
    method: Method.Post,
    module: Module.Lager,
    resource: ResourceUmbuchung.Umbuchung,
    select: new Set(['BelegNr']),
    data,
  });
}

export async function postUmbuchungPosition(belegNr: number, data: { Positionen: ApiUmbuchungSubmitPosition[] }) {
  await doApiRequest({
    method: Method.Post,
    module: Module.Lager,
    resource: ResourceUmbuchung.Umbuchung,
    id: belegNr,
    action: UmbuchungAction.Positionen,
    data,
  });
}

export async function putUmbuchungPosition(belegNr: number, posNr: number, data: ApiUmbuchungSubmitPosition) {
  await doApiRequest({
    method: Method.Put,
    module: Module.Lager,
    resource: ResourceUmbuchung.Umbuchung,
    id: belegNr,
    action: UmbuchungAction.UpdatePosition,
    actionParameters: {
      posNr,
    },
    data,
  });
}

export async function deleteUmbuchungPosition(id: number, posNr: number) {
  await doApiRequest({
    method: Method.Delete,
    module: Module.Lager,
    resource: ResourceUmbuchung.Umbuchung,
    id,
    action: UmbuchungAction.DeletePosition,
    actionParameters: {
      posNr,
    },
  });
}

export async function deleteUmbuchung(belegNr: number) {
  await doApiRequest({
    method: Method.Delete,
    module: Module.Lager,
    resource: ResourceUmbuchung.Umbuchung,
    id: belegNr,
  });
}
